import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty, noop } from 'lodash-es';
import { connect } from 'react-redux';
import { venomHistory } from 'client/utils/history/venom-history';
import { getAppraisalLink } from 'site-modules/shared/utils/appraisal-link-constructor';
import { checkVinInventory } from 'site-modules/shared/utils/appraisal/check-vin-inventory';
import { APPRAISAL_VEHICLE_ENTRY_CREATIVE_ID } from 'client/site-modules/shared/constants/appraisal/appraisal';
import { TrackingConstant } from 'client/tracking/constant';
import { AppraisalTabsContext } from 'site-modules/shared/components/appraisal/appraisal-tabs/appraisal-tabs-context';
import { AppraisalTabs } from 'site-modules/shared/components/appraisal/appraisal-tabs-container/appraisal-tabs';
import { usePrevious } from 'site-modules/shared/hooks/use-previous';
import { getQuery } from 'client/utils/location';
import { EventToolbox } from 'client/utils/event-toolbox';
import { getColorIdForUrl } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/utils/get-color-id-for-url';

export function AppraisalTabsContextProvider({
  WrappedComponent,
  ctaBtnColor = 'success',
  onFindVinModalOpen = noop,
  onFindVinModalClose = noop,
  onMultipleVinsModalOpen = noop,
  onMultipleVinsModalClose = noop,
  isOpenInNewTab = false,
  ...restProps
}) {
  const [vin, setVin] = useState('');
  const [isVinEdited, setIsVinEdited] = useState(false);
  const [wasVinPrePopulated, setWasVinPrePopulated] = useState(false);
  const [licensePlate, setLicensePlate] = useState('');
  const [stateCode, setStateCode] = useState(restProps.initialStateCode);
  const [mmy, setMmy] = useState({});
  const [addressInfo, setAddressInfo] = useState({
    firstName: '',
    lastName: '',
    address: '',
    apartment: '',
    // IMPORTANT: city should be undefined in order to detect user input.
    city: undefined,
  });
  const [styleIds, setStyleIds] = useState(null); // IMPORTANT: using null here to detect when styleIds are loaded.
  const [isReadyForRedirect, setIsReadyForRedirect] = useState(false);
  const [vinDecoderStyles, setVinDecoderStyles] = useState(null);
  const [vinStyles, setVinStyles] = useState(null);
  const [colors, setColors] = useState({});
  const [isAppraiserPageOpened, setIsAppraiserPageOpened] = useState(false);

  const { isAppExtensionPage, isVdpEmbedded, isLookUpStyleView, location, isProjectEveStyle, params, creativeId } =
    restProps;

  const prevIsReadyForRedirect = usePrevious(isReadyForRedirect);

  const setDefaultButtonStateForCachedPage = useCallback(({ persisted }) => {
    if (persisted) {
      setIsReadyForRedirect(false);
      setVin('');
      setLicensePlate('');
      setMmy({});
    }
  }, []);

  useEffect(() => {
    window.addEventListener('pageshow', setDefaultButtonStateForCachedPage);

    return () => {
      setIsReadyForRedirect(false);
      window.removeEventListener('pageshow', setDefaultButtonStateForCachedPage);
    };
  }, [setDefaultButtonStateForCachedPage]);

  const { exteriorColor, interiorColor } = colors ?? {};
  const exteriorColorId = exteriorColor?.id;
  const interiorColorId = interiorColor?.id;

  useEffect(() => {
    if (isReadyForRedirect && isReadyForRedirect !== prevIsReadyForRedirect) {
      const { make, model, year } = mmy;
      const { modsId, emo, mileage, condition } = getQuery(location);
      let styleIdsParam;

      if (!isEmpty(vinStyles)) {
        styleIdsParam = vinStyles?.results?.map(style => style?.styleId) ?? [];
      } else {
        styleIdsParam = styleIds;
      }

      const appraisalPathLink = getAppraisalLink({
        make,
        model,
        year,
        queryParams: {
          vin,
          styleIds: styleIdsParam?.join(','),
          modsId,
          mileage,
          condition,
          emo,
          colorId: getColorIdForUrl(exteriorColorId),
          interiorColorId: getColorIdForUrl(interiorColorId),
        },
        isAppExtensionPage,
      });

      if (isOpenInNewTab) {
        // Try to open in a NEW tab.
        const openedWindow = window.open(appraisalPathLink, '_blank');
        const isOpenedWithSuccess = !!openedWindow;
        if (isOpenedWithSuccess) {
          // eslint-disable-next-line react-hooks/set-state-in-effect -- responds to external browser API result (window.open); state change is conditional on pop-up blocker outcome
          setIsReadyForRedirect(false);
          setIsAppraiserPageOpened(true);
        } else {
          // Basically, this means that the tab was BLOCKED as a pop-up.
          EventToolbox.fireTrackAction({
            event_type: TrackingConstant.EVENT_TYPE_ACTION_END,
            event_data: {
              action_name: TrackingConstant.ACTION_APPRAISE_VEHICLE,
              action_category: TrackingConstant.USER_ACTION_CATEGORY,
              action_cause: TrackingConstant.ACTION_CAUSE_LINK_CLICK,
              subaction_name: 'new_tab_open_failure',
              creative_id: creativeId,
              value: 'popup blocker',
            },
          });
          // Open in the SAME tab.
          venomHistory.push(appraisalPathLink);
        }
      } else if (!isLookUpStyleView) {
        venomHistory.push(appraisalPathLink);
      }

      if (vin) {
        checkVinInventory(vin, APPRAISAL_VEHICLE_ENTRY_CREATIVE_ID, noop);
      }
    }
  }, [
    isAppExtensionPage,
    isLookUpStyleView,
    isReadyForRedirect,
    isVdpEmbedded,
    location,
    mmy,
    prevIsReadyForRedirect,
    styleIds,
    vin,
    vinStyles,
    creativeId,
    exteriorColorId,
    interiorColorId,
  ]);

  useEffect(() => {
    if (isAppraiserPageOpened) {
      // Reset this state after it was set to true.
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional one-shot pulse: set true to notify children, then auto-reset; children read the value as a prop so useRef is not viable
      setIsAppraiserPageOpened(false);
    }
  }, [isAppraiserPageOpened]);

  const queryVin = get(location, 'query.vin', '').toUpperCase();
  const contextProps = useMemo(
    () => ({
      licensePlate,
      setLicensePlate,
      mmy,
      setMmy,
      vin,
      setVin,
      isVinEdited,
      setIsVinEdited,
      queryVin,
      stateCode,
      setStateCode,
      styleIds,
      setStyleIds,
      vinDecoderStyles,
      setVinDecoderStyles,
      vinStyles,
      setVinStyles,
      addressInfo,
      setAddressInfo,
      isReadyForRedirect,
      setIsReadyForRedirect,
      isAppraiserPageOpened,
      ctaBtnColor,
      onFindVinModalOpen,
      onFindVinModalClose,
      onMultipleVinsModalOpen,
      onMultipleVinsModalClose,
      isProjectEveStyle,
      wasVinPrePopulated,
      setWasVinPrePopulated,
      params,
      setColors,
    }),
    [
      licensePlate,
      mmy,
      vin,
      isVinEdited,
      queryVin,
      stateCode,
      styleIds,
      vinDecoderStyles,
      vinStyles,
      addressInfo,
      isReadyForRedirect,
      isAppraiserPageOpened,
      ctaBtnColor,
      onFindVinModalOpen,
      onFindVinModalClose,
      onMultipleVinsModalOpen,
      onMultipleVinsModalClose,
      isProjectEveStyle,
      wasVinPrePopulated,
      params,
    ]
  );

  return (
    <AppraisalTabsContext.Provider value={contextProps}>
      <WrappedComponent
        {...restProps}
        ctaBtnColor={ctaBtnColor}
        onFindVinModalOpen={onFindVinModalOpen}
        onFindVinModalClose={onFindVinModalClose}
        onMultipleVinsModalOpen={onMultipleVinsModalOpen}
        onMultipleVinsModalClose={onMultipleVinsModalClose}
        isOpenInNewTab={isOpenInNewTab}
        vin={vin}
        setMmy={setMmy}
        setStyleIds={setStyleIds}
        setIsReadyForRedirect={setIsReadyForRedirect}
        setVin={setVin}
      />
    </AppraisalTabsContext.Provider>
  );
}

AppraisalTabsContextProvider.propTypes = {
  WrappedComponent: PropTypes.elementType.isRequired,
  ctaBtnColor: PropTypes.string,
  onFindVinModalOpen: PropTypes.func,
  onFindVinModalClose: PropTypes.func,
  onMultipleVinsModalOpen: PropTypes.func,
  onMultipleVinsModalClose: PropTypes.func,
  isOpenInNewTab: PropTypes.bool,
};

// A little hack to test AppraisalTabsContextProvider's logic that is passed to the context
export function AppraisalTabsContainerUI(props) {
  return <AppraisalTabsContextProvider {...props} WrappedComponent={AppraisalTabs} />;
}

export const mapStateToProps = state => ({
  isMobile: get(state, 'mobile'),
  location: get(state, 'pageContext.location'),
  referer: get(state, 'pageContext.page.referer'),
  profileOfferVin: get(state, 'profile.data.idm.appraisalOffersHistory[0].vin'),
  isAppExtensionPage: get(state, 'pageContext.isAppExtensionPage'),
});

export const AppraisalTabsContainer = connect(mapStateToProps)(AppraisalTabsContainerUI);
